/** Number items according to their order in the group/board */
export const MONDAY_COLUMN_TYPE = {
  AutoNumber: 'auto_number',
  /** Connect data from other boards */
  BoardRelation: 'board_relation',
  /** Perform actions on items by clicking a button */
  Button: 'button',
  /** Check off items and see what's done at a glance */
  Checkbox: 'checkbox',
  /** Manage a design system using a color palette */
  ColorPicker: 'color_picker',
  /** Choose a country */
  Country: 'country',
  /** Add the item's creator and creation date automatically */
  CreationLog: 'creation_log',
  /** Add dates like deadlines to ensure you never drop the ball */
  Date: 'date',
  /** Set up dependencies between items in the board */
  Dependency: 'dependency',
  /** Instantly add collaborative rich text editor */
  Doc: 'doc',
  /** Create a dropdown list of options */
  Dropdown: 'dropdown',
  /** Email team members and clients directly from your board */
  Email: 'email',
  /** Add files & docs to your item */
  File: 'file',
  /** Use functions to manipulate data across multiple columns */
  Formula: 'formula',
  Group: 'group',
  /** Add times to manage and schedule tasks, shifts and more */
  Hour: 'hour',
  /** Integration is really cool... */
  Integration: 'integration',
  /** Show all item's assignees */
  ItemAssignees: 'item_assignees',
  /** Show a unique ID for each item */
  ItemId: 'item_id',
  /** Add the person that last updated the item and the date */
  LastUpdated: 'last_updated',
  /** Simply hyperlink to any website */
  Link: 'link',
  /** Place multiple locations on a geographic map */
  Location: 'location',
  /** Add large amounts of text without changing column width */
  LongText: 'long_text',
  /** Show and edit columns' data from connected boards */
  Mirror: 'mirror',
  /** Name is really cool... */
  Name: 'name',
  /** Add revenue, costs, time estimations and more */
  Numbers: 'numbers',
  /** Assign people to improve team work */
  People: 'people',
  /** Assign a person to increase ownership and accountability (deprecated) */
  Person: 'person',
  /** Call your contacts directly from monday.com */
  Phone: 'phone',
  /** Show progress by combining status columns in a battery */
  Progress: 'progress',
  /** Rate or rank anything visually */
  Rating: 'rating',
  /** Get an instant overview of where things stand */
  Status: 'status',
  /** Use the subtasks column to create another level of tasks */
  Subtasks: 'subtasks',
  /** Add tags to categorize items across multiple boards */
  Tags: 'tags',
  /** Assign a full team to an item  */
  Team: 'team',
  /** Add textual information e.g. addresses, names or keywords */
  Text: 'text',
  /** Easily track time spent on each item, group, and board */
  TimeTracking: 'time_tracking',
  /** Visualize your item's duration, with a start and end date */
  Timeline: 'timeline',
  /** Unsupported column type */
  Unsupported: 'unsupported',
  /** Vote on an item e.g. pick a new feature or a favorite lunch place */
  Vote: 'vote',
  /** Select the week on which each item should be completed */
  Week: 'week',
  /** Keep track of the time anywhere in the world */
  WorldClock: 'world_clock',
} as const;

export type MondayColumnType =
  (typeof MONDAY_COLUMN_TYPE)[keyof typeof MONDAY_COLUMN_TYPE];

/**
 * Extended Monday column types that include custom types used by the app
 */
export const EXTENDED_MONDAY_COLUMN_TYPE = {
  ...MONDAY_COLUMN_TYPE,
  MirrorEmailColumn: 'mirror-email-column',
  MirrorNumericColumn: 'mirror-numeric-column',
  MirrorLinkColumn: 'mirror-link-column',
} as const;

export type ExtendedMondayColumnType =
  (typeof EXTENDED_MONDAY_COLUMN_TYPE)[keyof typeof EXTENDED_MONDAY_COLUMN_TYPE];

/** Mirror columns that use specialized API types; grouped as generic `mirror` in UI */
export const CUSTOM_MIRROR_COLUMN_TYPES = [
  EXTENDED_MONDAY_COLUMN_TYPE.MirrorEmailColumn,
  EXTENDED_MONDAY_COLUMN_TYPE.MirrorNumericColumn,
  EXTENDED_MONDAY_COLUMN_TYPE.MirrorLinkColumn,
] as const;

export const MIRROR_OR_RELATION_COLUMN_TYPES: readonly string[] = [
  EXTENDED_MONDAY_COLUMN_TYPE.Mirror,
  EXTENDED_MONDAY_COLUMN_TYPE.BoardRelation,
  ...CUSTOM_MIRROR_COLUMN_TYPES,
];

/** Human-readable labels for sidebar board field column types */
export const COLUMN_TYPE_LABELS: Record<string, string> = {
  [EXTENDED_MONDAY_COLUMN_TYPE.Date]: 'Date',
  [EXTENDED_MONDAY_COLUMN_TYPE.LongText]: 'Long Text',
  [EXTENDED_MONDAY_COLUMN_TYPE.Text]: 'Text',
  [EXTENDED_MONDAY_COLUMN_TYPE.Location]: 'Location',
  [EXTENDED_MONDAY_COLUMN_TYPE.Numbers]: 'Numbers',
  [EXTENDED_MONDAY_COLUMN_TYPE.ItemId]: 'Item ID',
  [EXTENDED_MONDAY_COLUMN_TYPE.Phone]: 'Phone',
  [EXTENDED_MONDAY_COLUMN_TYPE.Email]: 'Email',
  [EXTENDED_MONDAY_COLUMN_TYPE.Mirror]: 'Mirror',
  [EXTENDED_MONDAY_COLUMN_TYPE.Formula]: 'Formula',
  [EXTENDED_MONDAY_COLUMN_TYPE.Status]: 'Status',
  [EXTENDED_MONDAY_COLUMN_TYPE.Dropdown]: 'Dropdown',
  [EXTENDED_MONDAY_COLUMN_TYPE.BoardRelation]: 'Board Relation',
  [EXTENDED_MONDAY_COLUMN_TYPE.File]: 'File',
  [EXTENDED_MONDAY_COLUMN_TYPE.Link]: 'Link',
  [EXTENDED_MONDAY_COLUMN_TYPE.People]: 'People',
  [EXTENDED_MONDAY_COLUMN_TYPE.MirrorEmailColumn]: 'Mirror (Email)',
  [EXTENDED_MONDAY_COLUMN_TYPE.MirrorNumericColumn]: 'Mirror (Numbers)',
  [EXTENDED_MONDAY_COLUMN_TYPE.MirrorLinkColumn]: 'Mirror (Link)',
};
